export type PaymentDetailField = {
  label: string
  value: string
}

const push = (
  fields: PaymentDetailField[],
  label: string,
  value: unknown
) => {
  const text = value == null || value === '' ? '' : String(value).trim()
  if (!text) return
  fields.push({label, value: text})
}

const normalizePaymentObj = (raw: unknown): Record<string, any> | null => {
  if (raw == null || raw === '') return null
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) as Record<string, any>
    } catch {
      return null
    }
  }
  if (typeof raw === 'object') {
    const keys = Object.keys(raw as object)
    return keys.length > 0 ? (raw as Record<string, any>) : null
  }
  return null
}

const formatMoney = (amount: unknown, currency?: string): string => {
  const n = Number(amount)
  if (!Number.isFinite(n)) return ''
  const cur = currency ? String(currency).toUpperCase() : ''
  return cur ? `${n.toFixed(2)} ${cur}` : n.toFixed(2)
}

/** Pull human-readable rows from stored payment_obj (Stripe, stores, RevenueCat, etc.). */
export const extractPaymentPayloadFields = (
  paymentObj: unknown
): PaymentDetailField[] => {
  const obj = normalizePaymentObj(paymentObj)
  if (!obj) return []

  const fields: PaymentDetailField[] = []
  const id = String(obj.id ?? '')

  // Stripe PaymentIntent
  if (obj.object === 'payment_intent' || id.startsWith('pi_')) {
    push(fields, 'Payment intent ID', obj.id)
    push(fields, 'Stripe status', obj.status)
    const charge = obj.charges?.data?.[0]
    const card =
      charge?.payment_method_details?.card ??
      obj.payment_method_details?.card
    if (card?.brand) push(fields, 'Card brand', card.brand)
    if (card?.last4) push(fields, 'Card last 4', `**** ${card.last4}`)
    if (card?.exp_month && card?.exp_year) {
      push(fields, 'Card expiry', `${card.exp_month}/${card.exp_year}`)
    }
    if (obj.payment_method_types?.length) {
      push(fields, 'Payment method types', obj.payment_method_types.join(', '))
    }
    if (obj.description) push(fields, 'Description', obj.description)
    if (obj.receipt_email) push(fields, 'Receipt email', obj.receipt_email)
    const meta = obj.metadata
    if (meta && typeof meta === 'object') {
      if (meta.payment_as) push(fields, 'Payment category (metadata)', meta.payment_as)
      if (meta.user_id) push(fields, 'User ID (metadata)', meta.user_id)
      if (meta.subscription_plan_id && meta.subscription_plan_id !== 'null') {
        push(fields, 'Subscription plan ID', meta.subscription_plan_id)
      }
    }
    return fields
  }

  // Google Play subscription verify payload
  if (obj.orderId != null) {
    push(fields, 'Order ID', obj.orderId)
    push(fields, 'Product ID', obj.productId)
    push(fields, 'Package name', obj.packageName)
    if (obj.purchaseState != null) {
      push(fields, 'Purchase state', obj.purchaseState)
    }
    if (obj.priceAmountMicros != null) {
      const aud = Number(obj.priceAmountMicros) / 1_000_000
      push(
        fields,
        'Listed price',
        formatMoney(aud, obj.priceCurrencyCode)
      )
    }
    if (obj.purchaseTimeMillis) {
      push(
        fields,
        'Purchase time',
        new Date(Number(obj.purchaseTimeMillis)).toLocaleString()
      )
    }
    return fields
  }

  // RevenueCat / webhook style notification
  if (obj.type && (obj.store || obj.product_id || obj.original_app_user_id)) {
    push(fields, 'Event type', obj.type)
    push(fields, 'Store', obj.store)
    push(fields, 'Product ID', obj.product_id)
    if (obj.price != null) {
      push(fields, 'Price', formatMoney(obj.price, obj.currency))
    }
    if (obj.period_type) push(fields, 'Period type', obj.period_type)
    if (obj.transaction_id) push(fields, 'Store transaction ID', obj.transaction_id)
    if (obj.original_transaction_id) {
      push(fields, 'Original transaction ID', obj.original_transaction_id)
    }
    if (obj.purchased_at_ms) {
      push(
        fields,
        'Purchased at',
        new Date(Number(obj.purchased_at_ms)).toLocaleString()
      )
    }
    return fields
  }

  // Generic useful keys (legacy / unknown providers)
  const genericKeys: [string, string][] = [
    ['productId', 'Product ID'],
    ['subscriptionId', 'Subscription ID'],
    ['packageName', 'Package name'],
    ['orderId', 'Order ID'],
    ['transaction_id', 'Transaction ID'],
    ['type', 'Event type'],
    ['store', 'Store'],
    ['product_id', 'Product ID'],
    ['status', 'Provider status'],
    ['message', 'Message'],
  ]
  for (const [key, label] of genericKeys) {
    if (obj[key] != null && obj[key] !== '') push(fields, label, obj[key])
  }

  return fields
}
